import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus';
import type { PackageSet } from '@shipstation/client-common/types/seller-data';
import { Button, Group, Select } from '@mantine/core';

import { emptyArray } from 'ui-components/default-props/empty';

import { ModalPortal } from '../../../../../../../components/modal-portal';
import { MultiPackageModal, MULTI_PACKAGE_MODAL_CONTEXTS } from '../../../../../../../components/modals/multi-package-modal';
import { useModal } from '../../../../../../../hooks/use-modal';
import Global from '../../../../../../../store/global-selectors';
import { useProductDetailsContext } from '../../../../../use-product-details-context';
import { messages } from './messages';

export const PackageSetSection = () => {
  const intl = useIntl();
  const { packageSetId, setPackageSetId, userHasPermission, validationErrors, hasAttemptedSave } = useProductDetailsContext();
  const packageSets = useSelector(Global.selectSellerPackageSets) ?? emptyArray;
  const { isOpen: isCreateModalOpen, open: openCreateModal, close: closeCreateModal } = useModal();

  const packageSetOptions = useMemo(
    () => packageSets.map((packageSet: PackageSet) => ({ value: packageSet.packageSetId, label: packageSet.name })),
    [packageSets],
  );

  const packageSetError = hasAttemptedSave ? validationErrors?.packageSetId?.message : undefined;

  return (
    <Group align={packageSetError ? 'center' : 'flex-end'} gap='md'>
      <Select
        data={packageSetOptions}
        disabled={!userHasPermission}
        error={packageSetError}
        label={intl.formatMessage(messages.packageSetLabel)}
        onChange={setPackageSetId}
        placeholder={
          packageSetOptions.length === 0
            ? intl.formatMessage(messages.noSavedPackageSetsPlaceholder)
            : intl.formatMessage(messages.packageSetPlaceholder)
        }
        size='sm'
        value={packageSetId ?? null}
        w={320}
      />
      <Button disabled={!userHasPermission} leftSection={<FontAwesomeIcon icon={faPlus} />} onClick={openCreateModal} variant='tertiary'>
        {intl.formatMessage(messages.createPackageSetButtonLabel)}
      </Button>
      {isCreateModalOpen ? (
        <ModalPortal onClose={closeCreateModal}>
          <MultiPackageModal context={MULTI_PACKAGE_MODAL_CONTEXTS.Settings} onSave={closeCreateModal} />
        </ModalPortal>
      ) : null}
    </Group>
  );
};
